import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./styles.module.scss";
import PaymentsTable from "./PaymentTable";
import { RootState } from "typedefs";
import { useSelector } from "react-redux";
import {
  schedulingFetchGet,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import UserAvatar from "app/components/UserAvatar";
import { cls } from "app/utils";
import { useTranslation } from "react-i18next";
import SubscriptionCard from "./SubscriptionCard";
import OfferingCard from "./OfferingCard";
import { Loading } from "app/components/Wrappers";
import Navbar from "app/components/Navbar";
import SubscriptionCardWithRates from "./ProductsWithRateLinks/SubscriptionCardWithRates";
import OfferingCardWithRates from "./ProductsWithRateLinks/OfferingCardWithRates";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { CreditBundle } from "app/models/scheduling/CreditBundleModel";
import { scheduledOfferingsFromPurchases, transformCreditBundlesToOfferings } from "app/utils/creditBundles";

interface RouteParams {
  id: string;
}

interface RateLink {
  title: string;
  quantity: number;
  remainingSessions: number;
  purchaseId: string;
  id: number | null;
  unlimited: boolean;
}

interface Offering {
  offeringTitle: string;
  billingFrequency: number | null;
  nextInvoiceDate: string | null;
  expiredAt?: string | null;
  price?: number;
  remainingSessions: number;
  productPrice: number;
  currency: string;
  purchaseId?: string;
  customerId?: string;
  fullName?: string;
  profilePicture: string;
  unlimited?: boolean;
  productType?: string;
  productId?: string;
  coachId?: string;
  studentProfileId?: string;
  studentFullName?: string | null;
  rateLinks: RateLink[];
  productIntegerId: string;
  creditBundleIds: number[];
  completed?: boolean;
  cancelled?: boolean;
  // CN-2832: signed up before a custom start date; the first charge is on it.
  billingStartsAt?: string | null;
}

export default function CustomerDetails() {
  const { t } = useTranslation();
  const { id } = useParams<RouteParams>();
  const userId = useSelector((state: RootState) => state.user.self.id);
  const [purchases, setPurchases] = useState([]);
  const [subscriptions, setSubscriptions] = useState<Offering[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const history = useHistory();
  const usageQueue = useRef<Record<number, number>>({});
  const timer = useRef<NodeJS.Timeout | null>(null);
  const { firstName, lastName, email, profilePicture, currency, facilityId, stripeIntegrationEnabled } =
    location?.state || {};

  const loadCustomerData = useCallback(() => {
    // Build endpoints based on context (facility vs coach).
    // CFTB-3317: include_family=true returns the parent's offerings plus their
    // managed dependents', so the coach sees the whole household.
    const creditBundlesEndpoint = facilityId
      ? `/facilities/${facilityId}/students/${id}/credit_bundles?currency=${currency}&include_family=true`
      : `/student_profiles/${id}/credit_bundles?currency=${currency}&include_family=true`;

    const purchasesEndpoint = facilityId
      ? `/facilities/${facilityId}/students/${id}/purchases?currency=${currency}`
      : `/students/${id}/purchases?coach_id=${userId}&currency=${currency}`;

    Promise.all([
      schedulingFetchGet(creditBundlesEndpoint),
      schedulingFetchGet(purchasesEndpoint),
    ])
      .then(([bundles, purchaseData]: [CreditBundle[], any[]]) => {
        if (!purchaseData) {
          throw new Error("Unexpected response format");
        }
        setPurchases(purchaseData);

        // Keep each offering's OWN owner id (CFTB-3317: family members' offerings
        // must stay attributed to the dependent for session management, not the
        // parent); fall back to the page's customer id for the parent's own.
        // CN-2832: a subscription waiting for its custom start date has no
        // credits yet, so its card comes from payment history instead. A
        // customer with only such a subscription used to show nothing at all.
        const offerings = [
          ...transformCreditBundlesToOfferings(bundles || []),
          ...scheduledOfferingsFromPurchases(purchaseData),
        ].map((o) => ({
          ...o,
          studentProfileId: o.studentProfileId || id,
          profilePicture: o.profilePicture || "",
        }));
        setSubscriptions(offerings as Offering[]);
      })
      .catch((error) => {
        console.error("Failed to fetch data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, userId, facilityId, currency]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loading isLoading={loading} loadType="spinner" />
      </div>
    );
  }

  const handleOnCompleted = (creditBundleId: number) => {
    const newSubs = subscriptions.filter(sub =>  {
      if (!sub.creditBundleIds || sub.creditBundleIds.length === 0) {
        return true;
      }
      return !sub.creditBundleIds.includes(creditBundleId);
    });

    setSubscriptions(newSubs);
  };

  const handlePaymentUpdate = (updatedPayment: any) => {
    setPurchases((prev) =>
      prev.map((payment) =>
        payment.purchaseId === updatedPayment.purchaseId
          ? updatedPayment
          : payment
      )
    );
  };

  const formatInvoiceDate = (isoDate) => {
    if (!isoDate) {
      return t("N/A");
    }

    const date = new Date(isoDate);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const updateRemainingSessions = (
    purchaseId: string,
    incrementValue: number,
    rateLinkId?: number
  ) => {
    if (!rateLinkId) { return; }

    usageQueue.current[rateLinkId] = (usageQueue.current[rateLinkId] || 0) + incrementValue;

    setSubscriptions((prev) =>
      prev.map((sub) => {
        if (sub.purchaseId !== purchaseId) { return sub; }
        if (!sub.rateLinks) { return sub; }

        const updatedRateLinks = sub.rateLinks.map((rl) =>
          rl.id === rateLinkId
            ? { ...rl, remainingSessions: rl.remainingSessions + incrementValue }
            : rl
        );

        return { ...sub, rateLinks: updatedRateLinks };
      }).filter((sub) => {
        // An active subscription stays visible even when this cycle's sessions
        // hit 0 — it auto-renews and re-grants (mirrors the BE
        // CreditBundle.displayable scope). Only one-off offerings drop at 0.
        if (sub.productType === "subscription") { return true; }
        if (!sub.rateLinks || sub.rateLinks.length === 0) { return true; }
        return sub.rateLinks.some((rl) => rl.remainingSessions > 0 || rl.unlimited);
      })
    );

    if (timer.current) { clearTimeout(timer.current); }

    timer.current = setTimeout(() => {
      const promises = Object.entries(usageQueue.current)
        .filter(([, quantity]) => quantity !== 0)
        .map(([rateLinkIdStr, quantity]) => {
          const rateLinkId = parseInt(rateLinkIdStr, 10);
          const status = quantity > 0 ? "granted" : "used";
          const targetSub = subscriptions.find((s) =>
            s.rateLinks.some((r) => r.id === rateLinkId)
          );
          return schedulingFetchPost("/credit_bundle_usages", {
            stripe_product_id: targetSub.productIntegerId,
            student_profile_id: targetSub.studentProfileId,
            stripe_product_rate_link_id: rateLinkId,
            quantity: Math.abs(quantity),
            status,
          });
        });

      Promise.all(promises)
        .then(() => {
          toast.success(t("Sessions updated successfully."));
        })
        .catch((err) => {
          console.error("Failed to update usage:", err);
          toast.error(t("Failed to update sessions. Please try again."));
        });

      usageQueue.current = {};
    }, 600);
  };

  const handleDelete = (productId: string) => {
    // CN-2832: a scheduled card and its payment row both come from payment
    // history, so load it again: the card goes away and the row reads
    // "Cancelled", as after a page reload.
    if (subscriptions.some((sub) => sub.productId === productId && sub.billingStartsAt)) {
      loadCustomerData();
      return;
    }
    setSubscriptions((prevSubscriptions) =>
      prevSubscriptions.map((sub) =>
        sub.productId === productId ? { ...sub, nextInvoiceDate: null, cancelled: true } : sub
      )
    );
  };

  const navigateBack = () => {
    const backPath = facilityId ? "/facility_billing" : "/billing";
    history.push({
      pathname: backPath,
      state: { openCustomersTab: true },
    });
  };

  return (
    <div className={`billing-page-container`}>
      <Navbar label={t("Customer")} hasSearch={false} hasNotifications={false} onBackClick={navigateBack} />
      <div className={styles.customerDetailsContainer}>
        <div className={styles.customerHeader}>
          <div className={styles.customerInfo}>
            <UserAvatar
              srcName={profilePicture}
              userName={`${firstName} ${lastName}`}
              altName={t("avatar")}
              sizeHeight={10}
              sizeWidth={10}
              className={cls(styles.avatar)}
            />
            <div>
              <h1 className={styles.customerName}>
                {firstName} {lastName}
              </h1>
              {email && <p className={styles.customerEmail}>{email}</p>}
            </div>
          </div>
          {stripeIntegrationEnabled && (
            <div className={styles.actionButtons}>
              <ButtonVariant
                title={t('View in Stripe')}
                buttonType='primary-outline'
                onClickFunc={() => {
                  window.open(
                    `https://dashboard.stripe.com/customers/${subscriptions?.[0]?.customerId}`,
                    "_blank"
                  );
                }}
                buttonStyles={styles.actionButton}
              />
            </div>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {t("Subscriptions & Offerings")} ({subscriptions.length})
            </h2>
          </div>
          {subscriptions.length > 0 ? (
            subscriptions
              .map((sub, index) => (
                <div key={index} className={styles.marginForSubscriptionCard}>
                  {/* CFTB-3317: label offerings that belong to a managed family
                      member (anyone other than the page's customer). */}
                  {sub.studentProfileId &&
                    sub.studentProfileId !== id &&
                    sub.studentFullName && (
                      <p className={styles.familyMemberLabel}>{sub.studentFullName}</p>
                    )}
                  {sub.productType === "subscription" ? (
                    sub.rateLinks && sub.rateLinks.length > 0 ? (
                      <SubscriptionCardWithRates
                        subscription={sub}
                        formatInvoiceDate={formatInvoiceDate}
                        handleDelete={handleDelete}
                        updateRemainingSessions={updateRemainingSessions}
                        isCoach={true}
                        purchaseId={sub.purchaseId}
                      />
                    ) : (
                      <SubscriptionCard
                        subscription={sub}
                        formatInvoiceDate={formatInvoiceDate}
                        handleDelete={handleDelete}
                        remaining={sub.remainingSessions}
                        updateRemainingSessions={updateRemainingSessions}
                        purchaseId={sub.purchaseId}
                        isCoach={true}
                      />
                    )
                  ) : sub.rateLinks && sub.rateLinks.length > 0 ? (
                    <OfferingCardWithRates
                      subscription={sub}
                      updateRemainingSessions={updateRemainingSessions}
                      isCoach={true}
                      purchaseId={sub.purchaseId}
                      studentId={id}
                    />
                  ) : (
                    <OfferingCard
                      subscription={sub}
                      isCoach={true}
                      onCompleted={handleOnCompleted}
                    />
                  )}
                </div>
              ))
          ) : (
            <div className={styles.customers_placeholder}>
              <p className={styles.no_customers_text}>
                {t("This athlete does not have any active subscriptions or offerings.")}
              </p>
            </div>
          )}

        </div>
        <div className={styles.divider} />
        <div className={styles.section}>
          <h2 className={styles.sectionTitleWithMargin}>
            {t("Payments")} ({purchases?.length})
          </h2>
          {/* CFTB-3337: name the family member each purchase was for, matching the
              athlete payment page. The BE only sets familyMemberName for purchases
              made for a dependent, so a solo customer's rows stay unlabelled. */}
          <PaymentsTable
            payments={purchases}
            isCoach={true}
            onPaymentUpdate={handlePaymentUpdate}
            showFamilyMember
          />
        </div>
      </div>
    </div>
  );
}
